import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const ServiceContainer = styled.div`
  padding: var(--spacing-xxl) 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
`;

const PageTitle = styled.h1`
  margin-bottom: var(--spacing-md);
  
  span {
    color: var(--accent-lavender);
  }
`;

const IntroText = styled.p`
  max-width: 800px;
  margin-bottom: var(--spacing-xl);
  font-size: 1.2rem;
  color: var(--text-light);
`;

const ServiceSection = styled.section`
  margin-bottom: var(--spacing-xxl);
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xl);
  margin: var(--spacing-xl) 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Feature = styled.div`
  background: white;
  border-radius: 8px;
  padding: var(--spacing-lg);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const FeatureTitle = styled.h3`
  margin-bottom: var(--spacing-sm);
  color: var(--accent-lavender);
`;

const CaseStudiesSection = styled.section`
  margin-top: var(--spacing-xxl);
`;

const CaseStudyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-lg);
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const CardImage = styled.div`
  height: 200px;
  background-size: cover;
  background-position: center;
`;

const CardContent = styled.div`
  padding: var(--spacing-lg);
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  margin: var(--spacing-sm) 0;
  color: var(--text);
`;

const CardDescription = styled.p`
  color: var(--text-light);
  margin-bottom: var(--spacing-md);
`;

const CardStat = styled.div`
  font-weight: 600;
  color: var(--accent-lavender);
  margin: var(--spacing-sm) 0;
`;

const CardLink = styled(Link)`
  display: inline-block;
  background-color: var(--accent-turquoise);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 20px;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid var(--accent-turquoise);
  transition: all 0.3s ease;
  margin-top: var(--spacing-md);
  
  &:hover {
    background-color: transparent;
    color: var(--text);
  }
`;

const CTASection = styled.div`
  background-color: var(--secondary);
  padding: var(--spacing-xl);
  border-radius: 8px;
  text-align: center;
  margin-top: var(--spacing-xxl);
`;

const CTATitle = styled.h2`
  margin-bottom: var(--spacing-md);
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: var(--accent-turquoise);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid var(--accent-turquoise);
  transition: all 0.3s ease;
  margin-top: var(--spacing-md);
  
  &:hover {
    background-color: transparent;
    color: var(--text);
  }
`;

// Sample case studies specifically for Tourism Development
const caseStudies = [
  {
    id: 'eswatini',
    title: 'Eswatini Tourism',
    description: 'How familiarization trips connected local tour operators to global travel markets.',
    image: '/images/projects/eswatini-training.webp',
    keyStat: 'Fam trips connected local tours to 8 global operators'
  },
  {
    id: 'eswatini-digital',
    title: 'Eswatini Digital Marketing',
    description: 'Building sustainable digital marketing capacity for Eswatini Tourism Authority and local tourism businesses.',
    image: '/images/projects/eswatini-digital-analysis.webp',
    keyStat: 'Complete digital marketing framework for a destination'
  },
  {
    id: 'sri-lanka-undp',
    title: 'Sri Lanka National Tourism Policy',
    description: 'Developing Sri Lanka\'s tourism policy through data-driven analysis and stakeholder engagement in partnership with UNDP.',
    image: '/images/projects/sri-lanka-undp.webp',
    keyStat: 'Analyzed 400,000+ tourist reviews for policy insights'
  }
];

const TourismDevelopmentService = () => {
  return (
    <ServiceContainer>
      <Container>
        <PageTitle>Tourism <span>Development</span></PageTitle>
        <IntroText>
          Building bridges between local operators and global markets through sustainable, community-centered approaches.
          Our Tourism Development work empowers communities and small operators to meaningfully connect with global markets.
        </IntroText>
        
        <ServiceSection>
          <h2>How We Help</h2>
          <p>
            We design familiarization trips, create partnership strategies, and develop programs that support 
            economic growth while preserving local culture and environment.
          </p>
          
          <FeatureGrid>
            <Feature>
              <FeatureTitle>Familiarization Trips</FeatureTitle>
              <p>Design and organize immersive experiences that connect international buyers with local operators, creating long-term business relationships.</p>
            </Feature>
            <Feature>
              <FeatureTitle>Destination Strategy</FeatureTitle>
              <p>Develop comprehensive tourism strategies that balance economic development, community benefits, and environmental sustainability.</p>
            </Feature>
            <Feature>
              <FeatureTitle>Capacity Building</FeatureTitle>
              <p>Train local tourism stakeholders in digital marketing, product development, and quality service delivery to meet international market expectations.</p>
            </Feature>
            <Feature>
              <FeatureTitle>Policy Development</FeatureTitle>
              <p>Create evidence-based tourism policies that guide sustainable development and maximize benefits for local communities.</p>
            </Feature>
          </FeatureGrid>
        </ServiceSection>
        
        <CaseStudiesSection>
          <h2>Tourism Development Results</h2>
          <p>See how our tourism development work has created lasting connections and sustainable growth for destinations and operators.</p>
          
          <CaseStudyGrid>
            {caseStudies.map(study => (
              <Card key={study.id}>
                <Link to={`/projects/${study.id}`}>
                  <CardImage style={{ backgroundImage: `url(${study.image})` }} />
                </Link>
                <CardContent>
                  <CardTitle>{study.title}</CardTitle>
                  <CardDescription>{study.description}</CardDescription>
                  <CardStat>{study.keyStat}</CardStat>
                  <CardLink to={`/projects/${study.id}`}>View Case Study</CardLink>
                </CardContent>
              </Card>
            ))}
          </CaseStudyGrid>
        </CaseStudiesSection>
        
        <CTASection>
          <CTATitle>Ready to Develop Tourism Meaningfully?</CTATitle>
          <p>Let's discuss how our tourism development expertise can help your destination or community grow sustainably.</p>
          <CTAButton to="/contact">Start Your Project</CTAButton>
        </CTASection>
      </Container>
    </ServiceContainer>
  );
};

export default TourismDevelopmentService; 